import {envoyerLienMatomo, removeUndefined} from "./Utils.ts";
import {createErrorNotification} from "../../portail/js/Notifications.ts";

export type ajaxResponse = {
    body: ArrayBuffer | Blob | FormData | any | string
    response: Response
}

const getCookie = (name: string) => {
    let cookieValue = null;
    if (document.cookie && document.cookie != '') {
        const cookies = document.cookie.split(';');
        for (const element of cookies) {
            const cookie = element.trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) == (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

/**
 * Fonction qui gère les requêtes ajax afin de :
 * - Gérer les cas d'erreur, afficher la notification d'erreur et rejeter la promesse
 * - Gérer les différents formats de retour possible
 * - Gérer l'ajout du CSRF token
 */
const query = (url: string, params: RequestInit): Promise<ajaxResponse> => {
    return new Promise((resolve, reject) => {
        const csrftoken = getCookie('csrftoken');
        if (!params.headers) {
            params.headers = new Headers();
        }
        if (csrftoken) {
            (params.headers as Headers).append('X-CSRFToken', csrftoken);
        }
        (params.headers as Headers).append('X-Requested-With', 'XMLHttpRequest')

        envoyerLienMatomo('ajax', url, params.method)

        fetch(url, params).then(async (response) => {
            if (!response.ok) {
                const message = await response.text()
                const error: IAjaxError = {
                    code: response.status,
                    message,
                }
                if(window.Openscop.debug) console.error('erreur ajax : ', error)
                if (error.code === 430) {
                    createErrorNotification({
                        message: `${message}`,
                    })
                } else {
                    createErrorNotification({
                        message: `Ajax code d'erreur: ${error.code}`,
                    })
                }
                reject(error)
            } else {
                if (response.url.includes('accounts/two-factor/setup/')) {
                    location.href = response.url
                }
                const isJson = ['application/json', 'application/javascript'].includes(response.headers.get('content-type') ?? '');
                const isText = response.headers.get('content-type')?.includes('text');
                let body
                if (isJson) {
                    body = await response.json()
                } else if (isText) {
                    body = await response.text()
                } else {
                    body = await response.blob()
                }
                resolve({
                    body,
                    response,
                })
            }
        }).catch(error => {
            if (window.Sentry && window.Sentry.isInitialized()) {
                window.Sentry.captureException(error, {
                    contexts: {
                        parametres: {
                            ...params
                        },
                        ajax: {
                            url
                        },
                        error: {
                            error
                        }
                    }
                })
            }
            createErrorNotification({
                message: `Ajax code d'erreur: ${error.code}`,
            })
            reject(error)
        })
    })
}


export const $get = (url: string, params?: any, headers?: Headers) => {
    let completeUrl = url
    if (params) {
        if (params instanceof URLSearchParams) {
            completeUrl += `?${params.toString()}`
        } else {
            params = removeUndefined(params)
            completeUrl += `?${new URLSearchParams(params).toString()}`
        }
    }
    return query(completeUrl, {
        method: 'GET',
        headers,
    })
}

export const $post = (url: string, body?: any, headers?: Headers, params?: Partial<RequestInit>) => {
    if (params) params = removeUndefined(params)
    return query(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        ...params
    })
}

export const $postForm = (url: string, body?: FormData, headers?: Headers, params?: Partial<RequestInit>) => {
    if (params) params = removeUndefined(params)
    return query(url, {
        method: 'POST',
        headers,
        body,
        ...params
    })
}
